'use client';

import { FC, useMemo, useContext, useCallback, useState } from 'react';
import { FirmCustomStatusCellProps } from './FirmCustomStatusCell.types';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { Flex, Pill, Dropdown, ResultListItem } from '@/_components';
import { useRouter } from 'next/navigation';
import { saveQuestionnaire } from '@/_api/admin-questionnaires';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';
import { FirmCustomStatusUpdateDialog } from './components/FirmCustomStatusUpdateDialog';

export const FirmCustomStatusCell: FC<FirmCustomStatusCellProps> = ({
  questionnaire,
}) => {
  const { token } = useContext(UserContext);
  const { firm, firmCustomStatuses } = useContext(FirmSettingsContext);
  const { addToast } = useContext(ToastContext);
  const { updateAQuestionnaire } = useContext(QuestionnaireContext);

  const [firmStatusQuestionnaireIds, setFirmStatusQuestionnaireIds] = useState<
    Array<number>
  >([]);
  const [firmStatusId, setFirmStatusId] = useState<number | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);

  const { push } = useRouter();

  const { id, company_id, firm_custom_status_id } = questionnaire;

  const onUpdateStatusClick = useCallback(
    async (firm_custom_status_id: number | null) => {
      try {
        setLoading(true);

        await saveQuestionnaire({
          token,
          company_id,
          id,
          firm_custom_status_id,
        });

        updateAQuestionnaire({
          id,
          firm_custom_status_id,
        });
      } catch (error) {
        addToast('error', null, { error });
      } finally {
        setFirmStatusId(firm_custom_status_id);
        setFirmStatusQuestionnaireIds([id]);
        setLoading(false);
      }
    },
    [
      addToast,
      token,
      company_id,
      id,
      updateAQuestionnaire,
      setFirmStatusId,
      setFirmStatusQuestionnaireIds,
    ],
  );

  const firmStatusOptions: Array<ResultListItem> = useMemo(() => {
    const emptyFirmStatus: ResultListItem = {
      name: 'No Status',
      onClick: () => onUpdateStatusClick(null),
    };

    const firmStatuses: Array<ResultListItem> =
      firmCustomStatuses?.map((status) => {
        return {
          isActive: firm_custom_status_id === status.id,
          name: status.firm_user_display_status,
          value: status.id.toString(),
          onClick: () => onUpdateStatusClick(status.id),
        };
      }) || [];

    return [emptyFirmStatus, ...firmStatuses];
  }, [onUpdateStatusClick, firmCustomStatuses, firm_custom_status_id]);

  const selectedFirmStatusName: string = useMemo(
    () =>
      firm?.firm_custom_statuses?.find(
        (status) => status.id === firm_custom_status_id,
      )?.firm_user_display_status || 'No status',
    [firm?.firm_custom_statuses, firm_custom_status_id],
  );

  const onFirmStatusSettings = () => push('/firm');

  const hasFirmStatuses: boolean = !!firmStatusOptions?.length;

  if (!hasFirmStatuses) {
    firmStatusOptions.push({
      name: 'Firm status settings',
      onClick: onFirmStatusSettings,
    });
  }

  const hasFirmStatus: boolean =
    typeof firm_custom_status_id === 'number' || firm_custom_status_id !== null;

  const onCloseFirmStatusUpdate = () => {
    setFirmStatusId(null);
    setFirmStatusQuestionnaireIds([]);
  };

  return (
    <>
      <Dropdown items={firmStatusOptions} placement="left">
        <Flex isCentered={true}>
          <Pill
            label={selectedFirmStatusName}
            isLoading={isLoading}
            intent={hasFirmStatus ? 'info' : 'default'}
            isRound={true}
            size="small"
          />
        </Flex>
      </Dropdown>
      <FirmCustomStatusUpdateDialog
        questionnaireIds={firmStatusQuestionnaireIds}
        firmStatusId={firmStatusId}
        isOpen={
          firmStatusQuestionnaireIds?.length !== 0 &&
          typeof firmStatusId === 'number'
        }
        onClose={onCloseFirmStatusUpdate}
      />
    </>
  );
};
